/**
 * Saved views of the contact list (W4, B2-liste).
 *
 * A saved view is a named set of the list's own controls: the view tab
 * (À appeler / Rappels / Archivés), the filters, the search and the sort. It
 * is a per-browser convenience, like the hidden columns: it lives in
 * localStorage and never steers dialing by itself (applying one only sets the
 * same controls the agent could set by hand, and Power Mode dials the list
 * then shown, as before).
 *
 * Every read survives a missing or corrupted store: the list renders without
 * saved views rather than breaking.
 */

export type ContactView = 'default' | 'callbacks' | 'archived';

export const CONTACT_VIEWS: readonly ContactView[] = ['default', 'callbacks', 'archived'];

export interface ListFilters {
  leadStatus: string | string[];
  priority: string;
  lastCall: string | string[];
  score: string;
  persona: string | string[];
  city: string | string[];
}

export const EMPTY_LIST_FILTERS: ListFilters = {
  leadStatus: 'all', priority: 'all', lastCall: 'all', score: 'all', persona: 'all', city: 'all',
};

export interface SavedListView {
  id: string;
  name: string;
  view: ContactView;
  filters: ListFilters;
  search: string;
  sortBy: string | null;
  sortDirection: 'asc' | 'desc';
}

const STORAGE_KEY = 'xdial-saved-list-views';
const MAX_VIEWS = 20;

function isSavedView(v: unknown): v is SavedListView {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.name === 'string' && o.name.trim() !== ''
    && CONTACT_VIEWS.includes(o.view as ContactView)
    && !!o.filters && typeof o.filters === 'object';
}

export function readSavedViews(storage: Pick<Storage, 'getItem'> | null = safeStorage()): SavedListView[] {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedView).map((v) => ({
      ...v,
      filters: { ...EMPTY_LIST_FILTERS, ...v.filters },
      search: typeof v.search === 'string' ? v.search : '',
      sortBy: typeof v.sortBy === 'string' ? v.sortBy : null,
      sortDirection: v.sortDirection === 'asc' ? 'asc' : 'desc',
    }));
  } catch {
    return [];
  }
}

export function writeSavedViews(views: SavedListView[], storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try { storage?.setItem(STORAGE_KEY, JSON.stringify(views.slice(0, MAX_VIEWS))); } catch { /* storage unavailable */ }
}

/** Adds a view, or replaces the one with the same name (case-insensitive). Newest first. */
export function upsertSavedView(views: SavedListView[], view: Omit<SavedListView, 'id'>, id = newId()): SavedListView[] {
  const name = view.name.trim();
  const rest = views.filter((v) => v.name.trim().toLowerCase() !== name.toLowerCase());
  return [{ ...view, name, id }, ...rest].slice(0, MAX_VIEWS);
}

export function removeSavedView(views: SavedListView[], id: string): SavedListView[] {
  return views.filter((v) => v.id !== id);
}

function newId(): string {
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function safeStorage(): Storage | null {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}
